import React, { useEffect, useState, useRef } from 'react';
import AppShell from '@/components/AppShell';
import client from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { StatusBadge } from '@/components/StatusBadge';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Search, Plus, Loader2, ShieldCheck, AlertTriangle, ScanLine, Sparkles } from 'lucide-react';

const STATUS_TABS = [['active', 'Active'], ['terminated', 'Terminated'], ['pending_review', 'Pending Review'], ['pending_onboarding', 'Onboarding']];

export default function Drivers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('active');
  const [drivers, setDrivers] = useState(null);
  const [q, setQ] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const canManage = ['developer', 'company_admin', 'der', 'owner_operator'].includes(user?.role);

  const load = () => { setDrivers(null); client.get(`/drivers?status=${tab}`).then((r) => setDrivers(r.data)).catch(() => setDrivers([])); };
  useEffect(load, [tab]);

  const filtered = (drivers || []).filter((d) => d.full_name?.toLowerCase().includes(q.toLowerCase()));

  return (
    <AppShell title="Drivers">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>{STATUS_TABS.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`drivers-tab-${v}`}>{l}</TabsTrigger>)}</TabsList>
        </Tabs>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 rounded-lg border bg-white px-3 py-1.5"><Search className="h-4 w-4 text-muted-foreground" /><input placeholder="Search drivers" value={q} onChange={(e) => setQ(e.target.value)} className="w-40 bg-transparent text-sm outline-none" data-testid="drivers-search-input" /></div>
          {canManage && <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="drivers-bulk-scan-button"><ScanLine className="mr-1.5 h-4 w-4" />Bulk Scan</Button>}
          {canManage && <Button onClick={() => setAddOpen(true)} data-testid="drivers-add-button"><Plus className="mr-1.5 h-4 w-4" />Add Driver</Button>}
        </div>
      </div>

      <Card className="overflow-hidden">
        {drivers === null ? <div className="space-y-2 p-4">{[1,2,3,4].map(i=><Skeleton key={i} className="h-12" />)}</div> : filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">No drivers in this category.</div>
        ) : (
          <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Name</TableHead><TableHead>License</TableHead><TableHead>Status</TableHead><TableHead>Compliance</TableHead><TableHead>Quick Tags</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((d) => (
                <TableRow key={d.driver_id} className="cursor-pointer" onClick={() => navigate(`/drivers/${d.driver_id}`)} data-testid={`driver-row-${d.driver_id}`}>
                  <TableCell className="font-medium">{d.full_name}<div className="text-xs text-muted-foreground">{d.city}, {d.state}</div></TableCell>
                  <TableCell className="text-sm">{d.license_type}<div className="text-xs text-muted-foreground">{d.license_number}</div></TableCell>
                  <TableCell><StatusBadge status={d.status} /></TableCell>
                  <TableCell><StatusBadge status={d.compliance?.status} /></TableCell>
                  <TableCell><div className="flex flex-wrap gap-1">
                    {d.compliance?.expired?.length > 0 && <span className="rounded bg-[hsl(var(--danger))]/10 px-1.5 py-0.5 text-[10px] font-medium text-[hsl(var(--danger))]">{d.compliance.expired.length} expired</span>}
                    {d.compliance?.expiring?.length > 0 && <span className="rounded bg-[hsl(var(--warning))]/15 px-1.5 py-0.5 text-[10px] font-medium text-[hsl(var(--warning-foreground))]">{d.compliance.expiring.length} expiring</span>}
                    {d.compliance?.missing?.length > 0 && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">{d.compliance.missing.length} missing</span>}
                    {d.compliance?.shipping_unlocked && <span className="inline-flex items-center gap-0.5 rounded bg-[hsl(var(--success))]/10 px-1.5 py-0.5 text-[10px] font-medium text-[hsl(var(--success))]"><ShieldCheck className="h-3 w-3" />Cleared</span>}
                  </div></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        )}
      </Card>

      <AddDriverDialog open={addOpen} onOpenChange={setAddOpen} onAdded={load} />
      <BulkScanDialog open={bulkOpen} onOpenChange={setBulkOpen} onDone={load} />
    </AppShell>
  );
}

function AddDriverDialog({ open, onOpenChange, onAdded }) {
  const [f, setF] = useState({ first_name: '', last_name: '', email: '', phone: '', license_type: 'CDL A', license_number: '', state: 'Texas', license_state: 'Texas', city: '' });
  const [loading, setLoading] = useState(false);
  const [ocrType, setOcrType] = useState('CDL');
  const [ocrLoading, setOcrLoading] = useState(false);
  const [scanned, setScanned] = useState(null); // {file, doc_type, expires_at}
  const ocrRef = useRef();
  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const runScan = async (file) => {
    if (!file) return;
    setOcrLoading(true);
    const fd = new FormData(); fd.append('file', file); fd.append('doc_type', ocrType);
    try {
      const r = await client.post('/ocr/extract', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const fl = r.data.fields || {};
      let applied = 0;
      setF((s) => {
        const next = { ...s };
        ['first_name', 'middle_name', 'last_name', 'phone', 'dob', 'ssn', 'address', 'city', 'state', 'zip', 'license_number', 'license_state', 'license_type'].forEach((k) => {
          if (fl[k] && String(fl[k]).trim()) { next[k] = fl[k]; applied += 1; }
        });
        return next;
      });
      setScanned({ file, doc_type: ocrType, expires_at: r.data.expires_at });
      const unc = r.data.uncertain_fields || [];
      const labelMap = { CDL: 'CDL', MEDICAL_CARD: 'Medical Card', CLEARINGHOUSE: 'Clearinghouse query' };
      toast.success(`Autofilled ${applied} field(s) from the ${labelMap[ocrType] || 'document'}. The document will be attached on save.${unc.length ? ` Double-check: ${unc.join(', ')}.` : ''}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not read the document'); }
    finally { setOcrLoading(false); if (ocrRef.current) ocrRef.current.value = ''; }
  };

  const reset = () => { setF({ first_name: '', last_name: '', email: '', phone: '', license_type: 'CDL A', license_number: '', state: 'Texas', license_state: 'Texas', city: '' }); setScanned(null); };

  const submit = async () => {
    if (!f.first_name || !f.last_name || !f.email) { toast.error('First name, last name and email are required'); return; }
    setLoading(true);
    try {
      const res = await client.post('/drivers', f);
      const driverId = res.data.driver_id;
      // Attach the scanned document (with expiry) to the new driver, if any
      if (scanned?.file && driverId) {
        try {
          const dfd = new FormData();
          dfd.append('file', scanned.file);
          dfd.append('category', scanned.doc_type);
          dfd.append('driver_id', driverId);
          if (scanned.expires_at) dfd.append('expires_at', scanned.expires_at);
          dfd.append('replace', 'true');
          await client.post('/documents/upload', dfd, { headers: { 'Content-Type': 'multipart/form-data' } });
        } catch { toast.error('Driver added, but attaching the scanned document failed'); }
      }
      toast.success(`Driver added — onboarding pending${scanned ? ' · document attached' : ''}`);
      onAdded(); onOpenChange(false); reset();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent data-testid="add-driver-dialog" className="max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Add Driver</DialogTitle></DialogHeader>

        <div className="rounded-xl border border-[hsl(var(--primary))]/25 bg-[hsl(var(--primary))]/5 p-3" data-testid="add-driver-scan-card">
          <div className="flex items-center gap-2 text-sm font-medium"><Sparkles className="h-4 w-4 text-[hsl(var(--primary))]" />Scan a document to autofill</div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Select value={ocrType} onValueChange={setOcrType}>
              <SelectTrigger className="h-9 w-48" data-testid="add-driver-ocr-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="CDL">Commercial Driver's License</SelectItem><SelectItem value="MEDICAL_CARD">DOT Medical Card</SelectItem><SelectItem value="CLEARINGHOUSE">Clearinghouse Query</SelectItem></SelectContent>
            </Select>
            <Button type="button" variant="outline" size="sm" onClick={() => ocrRef.current?.click()} disabled={ocrLoading} data-testid="add-driver-scan-button">
              {ocrLoading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ScanLine className="mr-1.5 h-4 w-4" />}{ocrLoading ? 'Reading…' : 'Scan & Autofill'}
            </Button>
            {scanned && <span className="text-xs text-[hsl(var(--success))]">Document ready to attach{scanned.expires_at ? ` · exp ${scanned.expires_at}` : ''}</span>}
            <input ref={ocrRef} type="file" accept=".png,.jpg,.jpeg,.webp,.pdf" className="hidden" onChange={(e) => runScan(e.target.files[0])} data-testid="add-driver-ocr-input" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div><Label>First Name *</Label><Input value={f.first_name} onChange={(e) => upd('first_name', e.target.value)} className="mt-1" data-testid="add-driver-first" /></div>
          <div><Label>Last Name *</Label><Input value={f.last_name} onChange={(e) => upd('last_name', e.target.value)} className="mt-1" data-testid="add-driver-last" /></div>
          <div className="col-span-2"><Label>Email *</Label><Input value={f.email} onChange={(e) => upd('email', e.target.value)} className="mt-1" data-testid="add-driver-email" /></div>
          <div><Label>Phone</Label><Input value={f.phone} onChange={(e) => upd('phone', e.target.value)} className="mt-1" /></div>
          <div><Label>City</Label><Input value={f.city} onChange={(e) => upd('city', e.target.value)} className="mt-1" /></div>
          <div><Label>License Type</Label>
            <Select value={f.license_type} onValueChange={(v) => upd('license_type', v)}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{['CDL A', 'CDL B', 'CDL C'].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label>License #</Label><Input value={f.license_number} onChange={(e) => upd('license_number', e.target.value)} className="mt-1" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={submit} disabled={loading} data-testid="add-driver-submit">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Add Driver</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const EDIT_FIELDS = [
  ['first_name', 'First name'], ['last_name', 'Last name'], ['license_type', 'License type'],
  ['license_number', 'License #'], ['license_state', 'License state'], ['dob', 'DOB'],
];

function BulkScanDialog({ open, onOpenChange, onDone }) {
  const [step, setStep] = useState('upload'); // upload | review | done
  const [files, setFiles] = useState([]);
  const [docType, setDocType] = useState('CDL');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]); // reviewed rows {file, file_name, ok, reason, fields, email, expires_at, uncertain, include}
  const [results, setResults] = useState(null);
  const fileRef = useRef();

  const reset = () => { setStep('upload'); setFiles([]); setRows([]); setResults(null); setDocType('CDL'); };

  const parse = async () => {
    if (!files.length) { toast.error('Choose one or more document files'); return; }
    setLoading(true);
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    fd.append('doc_type', docType);
    try {
      const r = await client.post('/drivers/bulk-scan/parse', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const mapped = (r.data.results || []).map((res) => ({
        file: files[res.index], file_name: res.file, ok: !!res.ok, reason: res.reason || '',
        fields: res.fields || {}, email: '', expires_at: res.expires_at || '',
        uncertain: res.uncertain_fields || [], include: !!res.ok,
      }));
      setRows(mapped);
      setStep('review');
      const needAttn = mapped.filter((m) => m.ok && m.uncertain.length).length;
      toast.success(`Scanned ${mapped.length} document(s)${needAttn ? ` · ${needAttn} need review` : ''}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk scan failed'); }
    finally { setLoading(false); }
  };

  const setField = (i, key, val) => setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, fields: { ...r.fields, [key]: val } } : r));
  const setRow = (i, patch) => setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));

  const confirm = async () => {
    const kept = rows.filter((r) => r.include && r.ok);
    if (!kept.length) { toast.error('Select at least one driver to create'); return; }
    const missingName = kept.find((r) => !(r.fields.first_name || '').trim() && !(r.fields.last_name || '').trim());
    if (missingName) { toast.error('Every selected driver needs a first or last name'); return; }
    setLoading(true);
    const fd = new FormData();
    kept.forEach((r) => fd.append('files', r.file));
    fd.append('doc_type', docType);
    fd.append('records', JSON.stringify(kept.map((r) => ({ ...r.fields, email: r.email, expires_at: r.expires_at }))));
    try {
      const r = await client.post('/drivers/bulk-create', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResults(r.data); setStep('done');
      toast.success(`Created ${r.data.created} of ${r.data.total} driver profile(s)`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Create failed'); }
    finally { setLoading(false); }
  };

  const reviewCount = rows.filter((r) => r.ok && r.uncertain.length && r.include).length;
  const keptCount = rows.filter((r) => r.include && r.ok).length;

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent data-testid="bulk-scan-dialog" className="max-h-[92vh] max-w-2xl overflow-y-auto">
        <DialogHeader><DialogTitle>Bulk Onboarding {step === 'review' ? '· Review & Confirm' : step === 'done' ? '· Complete' : ''}</DialogTitle></DialogHeader>

        {step === 'upload' && (
          <div className="space-y-3">
            <div className="rounded-lg border border-[hsl(var(--primary))]/25 bg-[hsl(var(--primary))]/5 p-3 text-sm text-muted-foreground">Upload several CDLs (or Medical Cards) at once. We read each one and let you <b>review and edit</b> the details before any driver is created.</div>
            <div><Label>Document type</Label>
              <Select value={docType} onValueChange={setDocType}><SelectTrigger className="mt-1" data-testid="bulk-scan-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="CDL">Commercial Driver's License</SelectItem><SelectItem value="MEDICAL_CARD">DOT Medical Card</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label>Files (images or PDFs, up to 20)</Label>
              <input ref={fileRef} type="file" multiple accept=".png,.jpg,.jpeg,.webp,.pdf" onChange={(e) => setFiles(Array.from(e.target.files || []))} className="mt-1 block w-full text-sm" data-testid="bulk-scan-input" />
              {files.length > 0 && <p className="mt-1 text-xs text-muted-foreground">{files.length} file(s) selected</p>}
            </div>
          </div>
        )}

        {step === 'review' && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 rounded-lg border bg-muted/40 p-2.5 text-xs" data-testid="bulk-review-summary">
              <Sparkles className="h-4 w-4 text-[hsl(var(--primary))]" />
              <span><b>{keptCount}</b> ready to create{reviewCount > 0 && <span className="ml-1 text-[hsl(var(--warning-foreground))]">· {reviewCount} need a quick review</span>}</span>
            </div>
            <div className="max-h-[52vh] space-y-3 overflow-y-auto pr-1">
              {rows.map((r, i) => (
                <div key={i} className={`rounded-xl border p-3 ${!r.ok ? 'border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger))]/5' : r.include ? 'bg-white' : 'opacity-60'}`} data-testid={`bulk-review-row-${i}`}>
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 truncate text-xs font-medium text-muted-foreground"><ScanLine className="h-3.5 w-3.5" />{r.file_name}</span>
                    {r.ok ? (
                      <div className="flex items-center gap-2">
                        {r.uncertain.length > 0 && <span className="inline-flex items-center gap-1 rounded bg-[hsl(var(--warning))]/18 px-1.5 py-0.5 text-[10px] font-semibold text-[hsl(var(--warning-foreground))]"><AlertTriangle className="h-3 w-3" />Review</span>}
                        <Button type="button" size="sm" variant={r.include ? 'outline' : 'default'} className="h-7 px-2 text-xs" onClick={() => setRow(i, { include: !r.include })} data-testid={`bulk-review-toggle-${i}`}>{r.include ? 'Exclude' : 'Include'}</Button>
                      </div>
                    ) : <span className="inline-flex items-center gap-1 rounded bg-[hsl(var(--danger))]/12 px-1.5 py-0.5 text-[10px] font-semibold text-[hsl(var(--danger))]"><AlertTriangle className="h-3 w-3" />{r.reason || 'Unreadable'}</span>}
                  </div>
                  {r.ok && r.include && (
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      {EDIT_FIELDS.map(([k, label]) => {
                        const flag = r.uncertain.includes(k);
                        return (
                          <div key={k} className={k === 'first_name' || k === 'last_name' ? '' : ''}>
                            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}{flag && <span className="ml-1 text-[hsl(var(--warning-foreground))]">•</span>}</Label>
                            {k === 'license_type' ? (
                              <Select value={r.fields.license_type || 'CDL A'} onValueChange={(v) => setField(i, 'license_type', v)}>
                                <SelectTrigger className={`mt-0.5 h-8 text-xs ${flag ? 'border-[hsl(var(--warning))]' : ''}`}><SelectValue /></SelectTrigger>
                                <SelectContent>{['CDL A', 'CDL B', 'CDL C'].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                              </Select>
                            ) : (
                              <Input value={r.fields[k] || ''} onChange={(e) => setField(i, k, e.target.value)} className={`mt-0.5 h-8 text-xs ${flag ? 'border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/5' : ''}`} data-testid={`bulk-field-${k}-${i}`} />
                            )}
                          </div>
                        );
                      })}
                      <div>
                        <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Expires</Label>
                        <Input type="date" value={r.expires_at || ''} onChange={(e) => setRow(i, { expires_at: e.target.value })} className="mt-0.5 h-8 text-xs" data-testid={`bulk-field-expires-${i}`} />
                      </div>
                      <div className="col-span-2 sm:col-span-3">
                        <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Email (optional)</Label>
                        <Input value={r.email} onChange={(e) => setRow(i, { email: e.target.value })} placeholder="Leave blank to auto-generate" className="mt-0.5 h-8 text-xs" data-testid={`bulk-field-email-${i}`} />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 'done' && results && (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Created <b>{results.created}</b> of {results.total} profile(s). Each new driver is in the Onboarding tab with the scanned document attached.</p>
            <div className="max-h-72 space-y-1 overflow-y-auto">
              {results.results.map((r, i) => (
                <div key={i} className="flex items-center justify-between rounded border p-2 text-xs">
                  <span className="truncate">{r.name || r.file}</span>
                  <span className={r.status === 'created' ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--danger))]'}>{r.status === 'created' ? `${r.license_number || 'created'}${r.expires_at ? ` · exp ${r.expires_at}` : ''}` : `${r.status}: ${r.reason || ''}`}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 'upload' && (<>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={parse} disabled={loading} data-testid="bulk-scan-submit">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{loading ? 'Scanning…' : 'Scan & Review'}</Button>
          </>)}
          {step === 'review' && (<>
            <Button variant="outline" onClick={() => setStep('upload')} data-testid="bulk-review-back">Back</Button>
            <Button onClick={confirm} disabled={loading || keptCount === 0} data-testid="bulk-review-confirm">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{loading ? 'Creating…' : `Confirm & Create ${keptCount}`}{!loading && reviewCount > 0 && <span className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-[hsl(var(--warning))]/25 px-1.5 py-0.5 text-[10px] font-semibold text-[hsl(var(--warning-foreground))]"><AlertTriangle className="h-3 w-3" />{reviewCount} to review</span>}</Button>
          </>)}
          {step === 'done' && <Button onClick={() => { onOpenChange(false); reset(); }} data-testid="bulk-scan-done">Done</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
